<script setup>
import { computed } from 'vue';

import { storeToRefs } from 'pinia';

import { Check, Languages } from 'lucide-vue-next';

import { useLanguageStore } from '@/stores/language';

const languageStore = useLanguageStore();
const { currentLanguage } = storeToRefs(languageStore);
const { languages } = languageStore;

const activeShort = computed(
  () => languages.find((l) => l.code === currentLanguage.value)?.short ?? '',
);

const onSelect = (code) => languageStore.setLanguage(code);
</script>

<template>
  <el-dropdown
    trigger="click"
    placement="bottom-end"
    popper-class="lang-selector-dropdown"
    @command="onSelect"
  >
    <button
      class="lang-selector-trigger"
      type="button"
      :title="$t('Language')"
      :aria-label="$t('Language')"
    >
      <Languages
        class="lang-selector-icon"
        :size="18"
      />
      <span class="lang-selector-short">{{ activeShort }}</span>
    </button>

    <template #dropdown>
      <el-dropdown-menu>
        <el-dropdown-item
          v-for="lang in languages"
          :key="lang.code"
          :command="lang.code"
          :class="{ 'lang-selector-item--active': lang.code === currentLanguage }"
        >
          <span class="lang-selector-label">{{ lang.label }}</span>
          <Check
            v-if="lang.code === currentLanguage"
            class="lang-selector-check"
            :size="16"
          />
        </el-dropdown-item>
      </el-dropdown-menu>
    </template>
  </el-dropdown>
</template>

<style lang="scss" scoped>
.lang-selector-trigger {
  display: flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 10px;
  border: none;
  border-radius: 18px;
  background-color: var(--wp-surface-card);
  color: var(--wp-text-primary);
  font-size: 14px;
  font-weight: 500;
  line-height: 1;
  cursor: pointer;
  transition: background-color var(--transition-fast);

  &:hover {
    background-color: var(--wp-surface-hover);
  }
}

.lang-selector-icon {
  flex-shrink: 0;
}

.lang-selector-short {
  letter-spacing: 0.02em;
}
</style>

<!-- el-dropdown teleports its menu to <body>, so scoped styles can't reach the items.
     Keep these rules unscoped but namespaced under the popper-class to avoid leaking. -->
<style lang="scss">
.lang-selector-dropdown {
  .el-dropdown-menu__item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
  }

  .lang-selector-item--active {
    color: var(--wp-text-secondary);
    font-weight: 600;
  }

  .lang-selector-check {
    flex-shrink: 0;
  }
}
</style>
